import _ from "lodash";
/**--external-- */
import { graphql } from "lib/apolloClient";
import {
  CURRICULUM_TYPE_PYP,
  CACHE_AND_NETWORK,
  PLANNER_ELEMENT_NESTED_CHECKLIST,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
} from "Constants/stringConstants";

import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
/**--relative-- */
import {
  getNotificationsCountQuery,
  getPlannerElementSetsOfCurriculumQuery,
  getOrganizationStaffQuery,
  getPypPlannerThemeSetNodesQuery,
  getPypPlannerElementNodesOfCurriculumQuery,
  getUserRnpRolesQuery,
} from "modules/CommonQuery";
import {
  getOrganizationGlobalGenericTagsFromCache,
  getAllOrganizationFeedFromCache,
  getOrganizationStaffFromCache,
  getPypPlannerThemeSetNodesFromCache,
  getPypPlannerElementNodesFromCache,
} from "modules/CommonGraphqlHelpers";
import {
  getOrganizationGlobalGenericTagsQuery,
  getAllOrganizationFeedQuery,
} from "./CommonQuery";
import { prepareLoadMoreFeed } from "Utils";

export const getNotificationsCountQueryEnhancer = graphql(
  getNotificationsCountQuery,
  {
    name: "getNotificationsCount",
    alias: "getNotificationsCount",
    options: ({ userId, userEntityType, categoryTabs }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
        notificationCategoryFilter: categoryTabs,
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getNotificationsCount, ownProps: { isData, isLoading } }) {
      const notification = _.get(
        getNotificationsCount,
        "node.notification",
        {}
      );

      return {
        getNotificationsCount,
        onPusherEventTriggered: () => getNotificationsCount.refetch(),
        isData: !_.isEmpty(notification) && isData,
        notificationCount: _.get(notification, "newCount", 0),
        isLoading:
          getNotificationsCount["networkStatus"] == 1 ||
          getNotificationsCount["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }
);

export const getPypPlannerThemeSetNodesQueryEnhancer = graphql(
  getPypPlannerThemeSetNodesQuery,
  {
    name: "getPypPlannerThemeSetNodes",
    skip: ({ curriculumProgramType }) =>
      curriculumProgramType != CURRICULUM_TYPE_PYP,
    options: ({ plannerElementThemeSetId, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        themeSetId: plannerElementThemeSetId,
        filters: {
          academicYear: academicYearId, //added academicYear in filter will move it with other planner elements in near future.
        },
      },
    }),
    props({
      getPypPlannerThemeSetNodes,
      ownProps: { plannerElementThemeSetId, isData, isLoading, academicYearId },
    }) {
      const queryData = getPypPlannerThemeSetNodesFromCache({
        variables: {
          themeSetId: plannerElementThemeSetId,
          filters: {
            academicYear: academicYearId,
          },
        },
      });

      return {
        pypThemeSetNodes: _.get(queryData, "nodes", []),
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          isLoading ||
          _.includes([1, 2], getPypPlannerThemeSetNodes.networkStatus),
      };
    },
  }
);

const getPlannerElementSetsOfCurriculumVariables = ({
  curriculumId,
  curriculumNodeInterfaceType,
  plannerElementTypes,
  filters,
  portalType = "PLANNER",
}) => {
  return {
    id: curriculumId,
    type: curriculumNodeInterfaceType,
    elementTypes: plannerElementTypes,
    portalType,
    ...(!_.isEmpty(filters) && { filters }),
  };
};

export const getPlannerElementSetsOfCurriculumEnhancer = graphql(
  getPlannerElementSetsOfCurriculumQuery,
  {
    name: "getPlannerElementSetsOfCurriculum",
    skip: ({
      isStepWithPlannerElement,
      isDPLE,
      curriculumType,
      plannerElementTypes,
      skipQuery,
    }) =>
      (!isStepWithPlannerElement && !isDPLE) ||
      _.isEqual(curriculumType, CURRICULUM_TYPE_PYP) ||
      _.isEmpty(plannerElementTypes) ||
      _.includes(plannerElementTypes, PLANNER_ELEMENT_NESTED_CHECKLIST) ||
      !!skipQuery,
    options: ({
      curriculumId,
      plannerElementTypes,
      curriculumNodeInterfaceType,
      filters,
      portalType,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPlannerElementSetsOfCurriculumVariables({
        curriculumId,
        curriculumNodeInterfaceType,
        plannerElementTypes,
        filters,
        portalType,
      }),
    }),
    props({
      getPlannerElementSetsOfCurriculum,
      ownProps: { isData, isLoading },
    }) {
      const curriculumData = _.get(
        getPlannerElementSetsOfCurriculum,
        "node",
        {}
      );

      return {
        plannerElements: _.get(curriculumData, "plannerElementSets", []),
        isData: !_.isEmpty(curriculumData) && isData,
        isLoading:
          getPlannerElementSetsOfCurriculum["networkStatus"] == 1 ||
          getPlannerElementSetsOfCurriculum["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }
);

export const getOrganizationGlobalGenericTagsEnhancer = graphql(
  getOrganizationGlobalGenericTagsQuery,
  {
    name: "getOrganizationGlobalGenericTags",
    options: ({ globalTagFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: { globalTagFilters },
    }),
    props: ({
      getOrganizationGlobalGenericTags,
      ownProps: { globalTagFilters, isData, isLoading },
    }) => {
      const multiAccessIdentitiesTags = getOrganizationGlobalGenericTagsFromCache(
        {
          globalTagFilters,
        }
      );

      return {
        multiAccessIdentitiesTags,
        isData: !_.isEmpty(multiAccessIdentitiesTags) && isData,
        isLoading:
          _.includes(
            [1, 2],
            getOrganizationGlobalGenericTags["networkStatus"]
          ) || isLoading,
      };
    },
  }
);

export const getAllOrganizationFeedEnhancer = graphql(
  getAllOrganizationFeedQuery,
  {
    name: "getAllOrganizationFeed",
    options: ({ filters }) => ({
      fetchPolicy: "cache-and-network",
      variables: { filters },
    }),
    props: ({
      getAllOrganizationFeed,
      ownProps: { filters, isData, isLoading },
    }) => {
      const allOrganizationFeed = getAllOrganizationFeedFromCache({
        filters,
      });

      const organizationsData = _.get(allOrganizationFeed, "edges");

      const hasNextPage = _.get(allOrganizationFeed, "pageInfo.hasNextPage");
      const endCursor = _.get(allOrganizationFeed, "pageInfo.endCursor", "");
      const networkStatus = getAllOrganizationFeed["networkStatus"];

      return {
        organizationsData,
        isData: !_.isEmpty(allOrganizationFeed) && isData,
        networkStatus,
        hasNextPage,
        isLoading:
          _.includes([1, 2], getAllOrganizationFeed["networkStatus"]) ||
          isLoading,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: { filters: { ...filters, after: endCursor } },
          queryData: getAllOrganizationFeed,
          key: "allOrganizationFeed",
          nodeKey: "platform",
        }),
      };
    },
  }
);

export const getPYPplannerElementNodeOfCurriculumEnhancer = graphql(
  getPypPlannerElementNodesOfCurriculumQuery,
  {
    name: "getPypPlannerElementNodes",
    alias: "getPypPlannerElementNodes",
    skip: ({ curriculumType, plannerElementTypes, skipQuery }) =>
      !_.isEqual(curriculumType, CURRICULUM_TYPE_PYP) ||
      _.isEmpty(plannerElementTypes) ||
      _.includes(plannerElementTypes, ELEMENT_TYPE_PYP_LEARNING_STANDARD) ||
      _.includes(plannerElementTypes, PLANNER_ELEMENT_NESTED_CHECKLIST) ||
      !!skipQuery,
    options: ({
      plannerElementTypes,
      curriculumId,
      grades,
      isPypPlannerTheme,
      academicYearId,
    }) => {
      return {
        fetchPolicy: CACHE_AND_NETWORK,
        variables: {
          id: curriculumId,
          type: "IB_PYP_CURRICULUM_PROGRAM",
          elementTypes: plannerElementTypes,
          grades,
          portalType: "PLANNER",
          ...(isPypPlannerTheme ? { academicYear: academicYearId } : {}),
        },
      };
    },
    props: ({
      getPypPlannerElementNodes: { networkStatus },
      ownProps: {
        isData,
        isLoading,
        plannerElementTypes,
        curriculumId,
        grades,
        isPypPlannerTheme,
        academicYearId,
      },
    }) => {
      const data = getPypPlannerElementNodesFromCache({
        variables: {
          id: curriculumId,
          type: "IB_PYP_CURRICULUM_PROGRAM",
          elementTypes: plannerElementTypes,
          grades,
          ...(isPypPlannerTheme ? { academicYear: academicYearId } : {}),
        },
      });

      const plannerElementSets = _.get(data, "plannerElementSets", []);

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus) ||
          isLoading,
        plannerElements: plannerElementSets,
      };
    },
  }
);

export const getOrganizationStaffEnhancer = graphql(getOrganizationStaffQuery, {
  name: "getOrganizationStaff",
  skip: ({ orgStaffSkipCondition }) => !!orgStaffSkipCondition,
  options: ({ organizationId, orgStaffFilters }) => ({
    fetchPolicy: "cache-and-network",
    variables: { organizationId, filters: orgStaffFilters },
  }),
  props({
    getOrganizationStaff,
    ownProps: { organizationId, orgStaffFilters, isData, isLoading },
  }) {
    const data = getOrganizationStaffFromCache({
      organizationId,
      filters: orgStaffFilters,
    });
    const orgStaff = _.get(data, "node.staff.edges", []);
    const orgStaffTotalCount = _.get(data, "node.staff.totalCount", 0);
    return {
      isData: !_.isEmpty(data) && isData,
      isLoading:
        _.includes([1, 2], getOrganizationStaff["networkStatus"]) || isLoading,
      orgStaff,
      orgStaffTotalCount,
    };
  },
});

export const getUserRnpRolesEnhancer = graphql(getUserRnpRolesQuery, {
  name: "getUserRnpRoles",
  options: ({
    userId,
    userType,
    userRnpRolesFilter,
    portalType = "PLANNER",
  }) => ({
    fetchPolicy: "cache-and-network",
    variables: {
      id: userId,
      type: _.upperCase(userType),
      filters: userRnpRolesFilter,
      portalType,
    },
  }),
  skip: ({ isToddleLibrary }) => isToddleLibrary,
  props({
    getUserRnpRoles,
    ownProps: { userId, userType, userRnpRolesFilter, isData, isLoading },
  }) {
    const rnpRolesData = _.get(getUserRnpRoles, "node", {});
    return {
      isData: !_.isEmpty(rnpRolesData) && isData,
      isLoading:
        _.includes([1, 2], getUserRnpRoles["networkStatus"]) || isLoading,
      rnpRoleContextDetail: _.get(rnpRolesData, "rnpRoleContextDetail"),
    };
  },
});
